const fmtCas = t => (t || '').toString().slice(0, 5);

/**
 * Barvy fází. Ukončení je světlé, ne fialové — fialová se podle normy na
 * ploše nepoužívá (návrh „Horní panel směny", 2026-10-12). Hex, ne token:
 * pulz si k barvě přidává průhlednost (`${barva}40`).
 */
export const BARVA_FAZE = {
    zahajeni: '#FF8A00',
    prubeh:   '#10B981',
    ukonceni: '#E6E6EA',
};

const POradi = ['zahajeni', 'prubeh', 'ukonceni'];

/**
 * Osa fází směny — tři segmenty přes celou šířku a pod nimi jeden řádek.
 *
 * ```
 *   ██████████████  ██████████████  ░░░░░░░░░░░░░░
 *   Start 16:30            Průběh             Konec 23:00
 * ```
 *
 * Kolik zbývá, už nestojí v řádku osy — má vlastní místo vpravo nahoře
 * v panelu (`StalyPruh`). Řádek má pevnou velikost ve všech fázích, ať
 * panel nemění výšku.
 *
 * **Která fáze běží, se bere ze stavu směny, ne z `faze[].stav`.** Ten se
 * při clock-inu neaktualizuje — zahájení zůstává `todo`, i když má odklikané
 * všechny položky. Pořadí je pevné (zahájení → průběh → ukončení), takže
 * „hotovo" = všechno před aktuální fází.
 *
 * @param {Array} faze - `smena.faze`; z prvního a posledního se berou i časy
 * @param {string|null} aktivniFazeId - odvozeno ze stavu směny, ne z `stav`
 * @param {(faze) => void} [onOtevrit] - klik na segment (hotová = jen ke čtení)
 */
export default function OsaFazi({ faze = [], aktivniFazeId = null, onOtevrit }) {
    if (!faze.length) return null;

    const idxAktivni = aktivniFazeId ? POradi.indexOf(aktivniFazeId) : -1;
    const aktivni = idxAktivni >= 0 ? faze.find(f => f.id === aktivniFazeId) : null;

    const start = fmtCas(faze[0]?.cas_od);
    const konec = fmtCas(faze[faze.length - 1]?.cas_do);

    return (
        <div>
            {/* Proužek běžící fáze dýchá — ze tří segmentů je jen jeden „teď". */}
            <style>{`
                @keyframes osa-puls {
                    0%, 100% { box-shadow: 0 0 0 0 var(--puk-kruh-od); }
                    50%      { box-shadow: 0 0 0 3px var(--puk-kruh-do); }
                }
                .osa-puk-puls { animation: osa-puls 2.4s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) {
                    .osa-puk-puls { animation: none; }
                }
            `}</style>

            {/* Tlačítko má výšku na prst, vidět je z něj jen proužek. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {faze.map((f) => {
                    const poradi = POradi.indexOf(f.id);
                    const hotovo = idxAktivni >= 0 && poradi < idxAktivni;
                    const jeTed = poradi === idxAktivni;
                    const barva = BARVA_FAZE[f.id] || '#0CC0DF';
                    const lzeOtevrit = !!onOtevrit && (hotovo || jeTed);

                    return (
                        <button
                            key={f.id}
                            type="button"
                            onClick={lzeOtevrit ? () => onOtevrit(f) : undefined}
                            disabled={!lzeOtevrit}
                            title={`${f.nazev} · ${fmtCas(f.cas_od)}–${fmtCas(f.cas_do)}`}
                            aria-label={`${f.nazev}${jeTed ? ' — probíhá' : hotovo ? ' — hotovo' : ''}`}
                            style={{
                                flex: 1, minWidth: 0,
                                padding: '6px 0', margin: 0,
                                background: 'none', border: 'none',
                                cursor: lzeOtevrit ? 'pointer' : 'default',
                                WebkitTapHighlightColor: 'transparent',
                                display: 'block',
                            }}>
                            <span
                                className={jeTed ? 'osa-puk-puls' : undefined}
                                style={{
                                    display: 'block', height: 6, borderRadius: 3,
                                    background: hotovo || jeTed ? barva : 'var(--surface-elevated)',
                                    opacity: hotovo ? 0.45 : 1,
                                    ...(jeTed ? { '--puk-kruh-od': `${barva}00`, '--puk-kruh-do': `${barva}40` } : null),
                                }} />
                        </button>
                    );
                })}
            </div>

            <div style={{
                display: 'flex', alignItems: 'baseline', gap: 8,
                fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)',
                fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
            }}>
                <span>Start {start}</span>
                <span style={{
                    flex: 1, minWidth: 0, textAlign: 'center',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    color: 'var(--text-primary)', fontWeight: 800,
                }}>
                    {aktivni?.nazev}
                </span>
                <span>Konec {konec}</span>
            </div>
        </div>
    );
}
